import { create } from "zustand";
import type { StateCreator } from "zustand";
import { supabase } from "@/lib/supabase";
import { getErrorMessage } from "@/lib/errors/messages";
import { logError } from "@/lib/errors/handler";
// Core CRUD entity state
export interface CrudEntityState<T> {
  items: T[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
}

// Core CRUD entity actions
export interface CrudEntityActions<T> {
  fetchItems: () => Promise<void>;
  createItem: (item: Omit<T, "id">) => Promise<void>;
  updateItem: (id: string, updates: Partial<T>) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
}

// Combined CRUD entity store type
export type CrudEntityStore<T> = CrudEntityState<T> & CrudEntityActions<T>;

type OrderColumn = "position" | "sort_order";

/**
 * Creates a CRUD slice with standard operations for any entity
 * @param tableName - Database table name
 * @param orderColumn - Column for ordering (default: 'position')
 */
export function createCrudSlice<T extends { id: string; created_at?: string }>(
  tableName: string,
  orderColumn: OrderColumn = "position",
): StateCreator<CrudEntityStore<T>> {
  return (set, get) => ({
    items: [],
    isLoading: false,
    error: null,
    hasFetched: false,

    fetchItems: async () => {
      set({ isLoading: true, error: null });
      try {
        const { data, error } = await supabase
          .from(tableName)
          .select("*")
          .order(orderColumn, { ascending: true });
        if (error) throw error;
        set({ items: (data as T[]) || [], hasFetched: true });
      } catch (error: unknown) {
        logError("createCrudSlice.fetchItems", error);
        set({ error: getErrorMessage(error) });
      } finally {
        set({ isLoading: false });
      }
    },

    createItem: async (item) => {
      set({ isLoading: true, error: null });
      try {
        const { data: maxData } = await supabase
          .from(tableName)
          .select(orderColumn)
          .order(orderColumn, { ascending: false })
          .limit(1)
          .maybeSingle();
        const maxValue =
          ((maxData as unknown as Record<string, unknown> | null)?.[
            orderColumn
          ] as number) ?? 0;
        const insertData: Record<string, unknown> = {
          ...item,
          [orderColumn]: maxValue + 1,
        };
        const { error } = await supabase
          .from(tableName)
          .insert([insertData] as unknown as never[]);
        if (error) throw error;
        await get().fetchItems();
      } catch (error: unknown) {
        logError("createCrudSlice.createItem", error);
        set({ error: getErrorMessage(error) });
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    updateItem: async (id, updates) => {
      set({ isLoading: true, error: null });
      const oldItems = get().items;
      set((state) => ({
        items: state.items.map((item) =>
          item.id === id ? { ...item, ...updates } : item,
        ),
      }));
      try {
        const { error } = await supabase
          .from(tableName)
          .update(updates as never)
          .eq("id", id);
        if (error) throw error;
      } catch (error: unknown) {
        logError("createCrudSlice.updateItem", error);
        set({ items: oldItems, error: getErrorMessage(error) });
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    deleteItem: async (id) => {
      set({ isLoading: true, error: null });
      const oldItems = get().items;
      set((state) => ({ items: state.items.filter((item) => item.id !== id) }));
      try {
        const { error } = await supabase.from(tableName).delete().eq("id", id);
        if (error) throw error;
      } catch (error: unknown) {
        logError("createCrudSlice.deleteItem", error);
        set({ items: oldItems, error: getErrorMessage(error) });
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },
  });
}

/**
 * Creates a complex store by combining a CRUD slice with domain actions.
 */
export function createComplexStore<S extends CrudEntityStore<EntityOf<S>>>(
  crudSlice: StateCreator<CrudEntityStore<EntityOf<S>>>,
  domainActions: StateCreator<S>,
) {
  return create<S>((set, get, api) => {
    const crud = crudSlice(set, get, api);
    const domain = domainActions(
      set as unknown as Parameters<typeof domainActions>[0],
      get as unknown as Parameters<typeof domainActions>[1],
      api,
    );
    return { ...crud, ...domain } as S;
  });
}

/** Extracts the entity type from a CrudEntityStore shape. */
type EntityOf<S> = S extends CrudEntityStore<infer T> ? T : never;
